'use client';

import cn from 'classnames';
import { ChangeEvent, ChangeEventHandler, FocusEventHandler, useId } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Text } from '../Text';

const DEFAULT_COUNTRY = 'us';
const SEARCH_PLACEHOLDER = 'Search for your country';
const PREFERRED_COUNTRIES = ['us', 'fr', 'gb', 'ca', 'in', 'au', 'de', 'nl', 'br'];

export type PhoneFieldProps = {
  name: string;
  label?: string;
  value?: string;
  errorMessage?: string;
  wrapperClassName?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onNumberChange?: (phone: string) => void;
  onCountryChange?: (name: string, countryCode: string) => void;
}


export const PhoneField = ({
  name,
  label,
  value,
  errorMessage,
  wrapperClassName,
  onBlur,
  onChange,
  onNumberChange,
  onCountryChange,
}: PhoneFieldProps) => {
  const id = useId();

  return (
    <label htmlFor={id} className={cn('flex flex-col gap-y-2xs', wrapperClassName)}>
      {label && (
        <Text as="span" type="p2" className="text-primary">
          {label}
        </Text>
      )}
      <div
        // TODO: Check if there's better way to do this
        className={cn(
          'surface-l1 rounded-[6px] text-primary',
          '[&_.react-tel-input]:!w-full',
          '[&_.react-tel-input_.form-control]:!w-full',
          '[&_.react-tel-input_.form-control]:!h-auto',
          '[&_.react-tel-input_.form-control]:!bg-transparent',
          '[&_.react-tel-input_.form-control]:!border-0',
          '[&_.react-tel-input_.form-control]:!rounded-[6px]',
          '[&_.react-tel-input_.form-control]:!py-xs',
          '[&_.react-tel-input_.form-control]:!pl-[48px]',
          '[&_.react-tel-input_.form-control]:!pr-s',
          '[&_.react-tel-input_.form-control]:!font-sans',
          '[&_.react-tel-input_.form-control]:!text-current',
          '[&_.react-tel-input_.form-control:focus]:!shadow-none',
          '[&_.react-tel-input_.flag-dropdown]:!bg-transparent',
          '[&_.react-tel-input_.flag-dropdown]:!border-0',
          '[&_.react-tel-input_.flag-dropdown]:!rounded-l-[6px]',
          '[&_.react-tel-input_.selected-flag]:!bg-transparent',
          '[&_.react-tel-input_.selected-flag]:!rounded-l-[6px]',
          '[&_.react-tel-input_.selected-flag:hover]:!bg-black/10',
          '[&_.react-tel-input_.selected-flag.open]:!bg-black/10',
          '[&_.react-tel-input_.country-list]:!bg-white',
          '[&_.react-tel-input_.country-list]:!text-black',
          '[&_.react-tel-input_.country-list]:!rounded-[6px]',
        )}
      >
        <PhoneInput
          inputProps={{ id, name }}
          country={DEFAULT_COUNTRY}
          value={value ?? ''}
          onChange={(phone, data: any, event) => {
            onNumberChange?.(phone);
            onChange?.(event as ChangeEvent<HTMLInputElement>);

            if (data && 'countryCode' in data) {
              onCountryChange?.(data.name, data.countryCode);
            }
          }}
          onBlur={onBlur}
          isValid
          preferredCountries={PREFERRED_COUNTRIES}
          searchPlaceholder={SEARCH_PLACEHOLDER}
          preserveOrder={['preferredCountries']}
          specialLabel=""
          enableSearch
          disableSearchIcon
        />
      </div>
      {errorMessage && (
        <Text as="span" type="caption2" className="text-red-500">
          {errorMessage}
        </Text>
      )}
    </label>
  );
};

export default PhoneField;
